/*
  Resolves how and when an opportunity bonus pays out. A result accepted under Strategies carries its own
  terms for life (netPaymentTerms, payoutPolicy, payoutReleasableAt stamped on the row); everything else
  follows the brand's fronted-invoice state exactly as the web and node getOpportunityNetPaymentTerms do.
*/

const _ = require('lodash');
const { isStrategyOpportunity } = require('./strategy_opportunities');

const OPPORTUNITY_PAYOUT_POLICIES = {
  BRAND_TERMS: 'brand_terms',
  STRATEGY_DELAYED: 'strategy_delayed',
  STRATEGY_SAME_WEEK: 'strategy_same_week'
};

const DEFAULT_NET_PAYMENT_TERMS = 30;

const STRATEGY_PAYOUT_POLICIES = [OPPORTUNITY_PAYOUT_POLICIES.STRATEGY_DELAYED, OPPORTUNITY_PAYOUT_POLICIES.STRATEGY_SAME_WEEK];

const isStrategyPayoutPolicy = policy => STRATEGY_PAYOUT_POLICIES.includes(policy);

const isActiveStrategySettings = settings => !!settings?.activatedAt && !settings?.deactivatedAt;

const getStrategyPayoutPolicyForDelayDays = payoutDelayDays =>
  payoutDelayDays === 0 ? OPPORTUNITY_PAYOUT_POLICIES.STRATEGY_SAME_WEEK : OPPORTUNITY_PAYOUT_POLICIES.STRATEGY_DELAYED;

// A bonus exists only once its result completes, and a review period defers completion to its end. The weekly payout
// run (Friday 19:00 UTC, production_payments.yml) pays every bonus already marked paid: a same-week completion marks
// its bonus paid on the spot, so it is in the first Friday run at or after it exists; a delayed bonus waits for the
// nightly release run (04:00 UTC, production_lock_unpaid_bonuses.yml) at or after both its release date and its
// existence, then for the Friday run after that. That is the date every surface promises.
const RELEASE_RUN_HOUR_UTC = 4;
const PAYOUT_RUN_WEEKDAY_UTC = 5;
const PAYOUT_RUN_HOUR_UTC = 19;
// The hourly opportunity stats run (production_opportunity_stats.yml) is what marks a review complete
const REVIEW_COMPLETION_CHECK_INTERVAL_MS = 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const toValidDate = value => {
  if (_.isNil(value) || value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/*
  releasableAt: the stamped or projected payoutReleasableAt (ignored for a same-week bonus)
  readyAt:      when the bonus can first exist, from getPayoutReadyAt
  sameWeek:     true for a same-week bonus, which skips the nightly release

  YYYY-MM-DD (UTC) of the payout run that pays the bonus; null without the dates that decide it.
*/
const getExpectedPayoutDate = ({ releasableAt, readyAt, sameWeek = false } = {}) => {
  const ready = toValidDate(readyAt);
  const releasable = toValidDate(releasableAt);
  let payableFrom = null;
  if (sameWeek) payableFrom = ready;
  else if (releasable) payableFrom = nextReleaseRunAtOrAfter(ready && ready > releasable ? ready : releasable);
  if (!payableFrom) return null;
  return nextPayoutRunAtOrAfter(payableFrom)
    .toISOString()
    .slice(0, 10);
};

const nextReleaseRunAtOrAfter = moment => {
  const run = new Date(Date.UTC(moment.getUTCFullYear(), moment.getUTCMonth(), moment.getUTCDate(), RELEASE_RUN_HOUR_UTC));
  if (run < moment) run.setUTCDate(run.getUTCDate() + 1);
  return run;
};

const nextPayoutRunAtOrAfter = moment => {
  const run = new Date(Date.UTC(moment.getUTCFullYear(), moment.getUTCMonth(), moment.getUTCDate(), PAYOUT_RUN_HOUR_UTC));
  if (run < moment) run.setUTCDate(run.getUTCDate() + 1);
  run.setUTCDate(run.getUTCDate() + ((PAYOUT_RUN_WEEKDAY_UTC - run.getUTCDay() + 7) % 7));
  return run;
};

// A result whose requirements are met but whose completion, and so its bonus, waits for the review period to end
const isOpportunityResultInReviewPeriod = result => !!toValidDate(result?.reviewStart) && !result?.isCompleted && _.isNil(result?.isCompletedAt);

// When the bonus can first exist: the completion time, or, while the result is in review, the end of the hourly
// stats run that starts at the top of the hour after the review ends (the run has the hour to reach the row, and a
// run that starts as a payout run starts must not be counted as caught); null before either is known.
const getPayoutReadyAt = (result, daysToReviewUponCompletion) => {
  if (!isOpportunityResultInReviewPeriod(result)) return toValidDate(result?.isCompletedAt);
  const reviewEnd = toValidDate(result.reviewStart).getTime() + Number(daysToReviewUponCompletion || 0) * DAY_MS;
  return new Date((Math.ceil(reviewEnd / REVIEW_COMPLETION_CHECK_INTERVAL_MS) + 1) * REVIEW_COMPLETION_CHECK_INTERVAL_MS);
};

// The stamped release date, or the one completion will stamp: the clock starts at review start (completion when
// there is no review) and runs netPaymentTerms days, the arithmetic recordOpportunityStrategyCompletionSpend uses.
const getProjectedPayoutReleasableAt = (result, netPaymentTerms) => {
  if (!_.isNil(result?.payoutReleasableAt)) return result.payoutReleasableAt;
  const anchor = toValidDate(result?.reviewStart ?? result?.isCompletedAt);
  if (!anchor || _.isNil(netPaymentTerms)) return null;
  return new Date(anchor.getTime() + Number(netPaymentTerms) * DAY_MS).toISOString();
};

// "Friday, November 20, 2026" for a YYYY-MM-DD or ISO date; null when the date does not parse.
const formatPayoutDate = (date, { weekday = true } = {}) => {
  const parsed = toValidDate(typeof date === 'string' && date.length === 10 ? `${date}T00:00:00Z` : date);
  if (!parsed) return null;
  return parsed.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    ...(weekday ? { weekday: 'long' } : {}),
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

// The payout sentences CX and Marketing approved for Strategies creators (Slack, Sept 23 2026). Web, mobile and
// the emails all render these, so no surface can promise a different day than another.
const PAYOUT_TIMING_PREFIX = 'Payout timing: ';

const describePayoutTiming = ({ policy, netPaymentTerms }) => {
  if (policy === OPPORTUNITY_PAYOUT_POLICIES.STRATEGY_SAME_WEEK) return 'You will be paid the Friday after you complete the requirements.';
  if (policy === OPPORTUNITY_PAYOUT_POLICIES.STRATEGY_DELAYED && !_.isNil(netPaymentTerms))
    return `You will be paid in the first scheduled payout after ${netPaymentTerms} days from completing the requirements.`;
  return null;
};

// "You will be paid $1,250 on Friday, November 20, 2026.", falling back to the timing sentence when the date is not
// known yet; `amount` is optional, and the emails, chat lines and web copy all render this one promise.
const describePayoutPromise = ({ expectedPayoutDate, netPaymentTerms, policy = OPPORTUNITY_PAYOUT_POLICIES.STRATEGY_DELAYED, amount } = {}) => {
  const formattedAmount = _.isNil(amount) ? null : `$${Number(amount).toLocaleString('en-US')}`;
  const paid = formattedAmount ? `You will be paid ${formattedAmount}` : 'You will be paid';
  const formattedDate = expectedPayoutDate ? formatPayoutDate(expectedPayoutDate) : null;
  if (formattedDate) return `${paid} on ${formattedDate}.`;
  const timing = describePayoutTiming({ policy, netPaymentTerms });
  if (timing) return timing.replace('You will be paid', paid);
  return formattedAmount ? `Your bonus of ${formattedAmount} is waiting for its payout date.` : 'Your bonus is waiting for its payout date.';
};

const describeHeldBonus = options => `You've completed the requirements. ${describePayoutPromise(options)}`;

/*
  result:                     the OpportunityResult row when the request has been accepted, else null
  opportunity:                the Opportunity row; only one a Strategy runs (strategyKey set) is offered the brand's Strategies terms
  brandPayoutInfo:            the brand's payout_info relation ({ oppsNetPaymentTerms, netPaymentTerms, instant, strategyPayoutDelayDays });
                              strategyPayoutDelayDays is the brand's active Strategies delay, null when the brand is not active
  defaultNetPaymentTerms:     fallback when the brand has no terms configured
  daysToReviewUponCompletion: the opportunity's review period, which defers the bonus while the result is in review

  Returns { policy, netPaymentTerms, isInstant, releasableAt, expectedPayoutDate }. releasableAt is the row's
  payoutReleasableAt, null until completion stamps it, and always null outside Strategies. expectedPayoutDate is
  the YYYY-MM-DD of the payout run that pays the bonus, projected from the review start or completion when the
  stamp is not there yet, and null before completion or outside Strategies.
*/
const getEffectivePayoutPolicy = ({
  result,
  opportunity,
  brandPayoutInfo,
  defaultNetPaymentTerms = DEFAULT_NET_PAYMENT_TERMS,
  daysToReviewUponCompletion
} = {}) => {
  if (result && isStrategyPayoutPolicy(result.payoutPolicy)) {
    const netPaymentTerms = _.isNil(result.netPaymentTerms) ? null : result.netPaymentTerms;
    return {
      policy: result.payoutPolicy,
      netPaymentTerms,
      isInstant: netPaymentTerms === 0,
      releasableAt: _.isNil(result.payoutReleasableAt) ? null : result.payoutReleasableAt,
      expectedPayoutDate: getExpectedPayoutDate({
        releasableAt: getProjectedPayoutReleasableAt(result, netPaymentTerms),
        readyAt: getPayoutReadyAt(result, daysToReviewUponCompletion),
        sameWeek: result.payoutPolicy === OPPORTUNITY_PAYOUT_POLICIES.STRATEGY_SAME_WEEK
      })
    };
  }

  const activeStrategyPayoutDelayDays = brandPayoutInfo?.strategyPayoutDelayDays ?? null;
  if (!result && isStrategyOpportunity(opportunity) && !_.isNil(activeStrategyPayoutDelayDays)) {
    return {
      policy: getStrategyPayoutPolicyForDelayDays(activeStrategyPayoutDelayDays),
      netPaymentTerms: activeStrategyPayoutDelayDays,
      isInstant: activeStrategyPayoutDelayDays === 0,
      releasableAt: null,
      expectedPayoutDate: null
    };
  }

  const hasStampedTerms = !_.isNil(result?.netPaymentTerms);
  const netPaymentTerms = hasStampedTerms
    ? result.netPaymentTerms
    : brandPayoutInfo?.oppsNetPaymentTerms ?? brandPayoutInfo?.netPaymentTerms ?? defaultNetPaymentTerms;
  const isInstant = hasStampedTerms ? result.netPaymentTerms === 0 : netPaymentTerms === 0 || !!brandPayoutInfo?.instant;

  return {
    policy: OPPORTUNITY_PAYOUT_POLICIES.BRAND_TERMS,
    netPaymentTerms,
    isInstant,
    releasableAt: null,
    expectedPayoutDate: null
  };
};

module.exports = {
  OPPORTUNITY_PAYOUT_POLICIES,
  STRATEGY_PAYOUT_POLICIES,
  isStrategyPayoutPolicy,
  isActiveStrategySettings,
  getStrategyPayoutPolicyForDelayDays,
  getEffectivePayoutPolicy,
  getExpectedPayoutDate,
  getPayoutReadyAt,
  getProjectedPayoutReleasableAt,
  isOpportunityResultInReviewPeriod,
  formatPayoutDate,
  PAYOUT_TIMING_PREFIX,
  describePayoutTiming,
  describePayoutPromise,
  describeHeldBonus
};
